import React, { useState } from 'react';
import {
  Search, Book, MessageCircle, Phone, Mail, ChevronRight, ArrowLeft,
  HelpCircle, FileText, Settings, ShoppingCart, BarChart3, CreditCard
} from 'lucide-react';

const Helpcenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Book,
      colorClass: 'text-primary bg-opacity-10 bg-primary',
      articles: [
        { id: 1, title: 'Setting up your ONDC seller account', content: 'Learn how to create and configure your seller account on the ONDC network...' },
        { id: 2, title: 'Understanding ONDC marketplace', content: 'Get familiar with how the Open Network for Digital Commerce operates...' },
        { id: 3, title: 'First steps after registration', content: 'Complete these essential steps to start selling on ONDC...' }
      ]
    },
    {
      id: 'orders',
      title: 'Order Management',
      icon: ShoppingCart,
      colorClass: 'text-success bg-opacity-10 bg-success',
      articles: [
        { id: 4, title: 'Processing incoming orders', content: 'Step-by-step guide to handle new orders efficiently...' },
        { id: 5, title: 'Managing order cancellations', content: 'How to handle order cancellations and refunds...' },
        { id: 6, title: 'Order tracking and updates', content: 'Keep customers informed about their order status...' }
      ]
    },
    {
      id: 'products',
      title: 'Product Management',
      icon: FileText,
      colorClass: 'text-purple bg-opacity-10 bg-purple',
      articles: [
        { id: 7, title: 'Adding products to catalog', content: 'How to list your products on the ONDC network...' },
        { id: 8, title: 'Product categorization', content: 'Properly categorize your products for better visibility...' },
        { id: 9, title: 'Inventory management', content: 'Keep track of your stock levels and availability...' }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: BarChart3,
      colorClass: 'text-warning bg-opacity-10 bg-warning',
      articles: [
        { id: 10, title: 'Understanding your dashboard', content: 'Navigate through your sales analytics and insights...' },
        { id: 11, title: 'Sales reports and trends', content: 'Analyze your performance with detailed reports...' },
        { id: 12, title: 'Customer insights', content: 'Understand your customer behavior and preferences...' }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Settlements',
      icon: CreditCard,
      colorClass: 'text-info bg-opacity-10 bg-info',
      articles: [
        { id: 13, title: 'Payment processing', content: 'How payments work in the ONDC ecosystem...' },
        { id: 14, title: 'Settlement cycles', content: 'Understanding when and how you get paid...' },
        { id: 15, title: 'Transaction disputes', content: 'Handle payment disputes and chargebacks...' }
      ]
    },
    {
      id: 'account',
      title: 'Account Settings',
      icon: Settings,
      colorClass: 'text-secondary bg-opacity-10 bg-secondary',
      articles: [
        { id: 16, title: 'Profile management', content: 'Update your business profile and contact information...' },
        { id: 17, title: 'Security settings', content: 'Secure your account with proper authentication...' },
        { id: 18, title: 'Notification preferences', content: 'Customize how you receive important updates...' }
      ]
    }
  ];

  const quickActions = [
    { title: 'Contact Support', icon: MessageCircle },
    { title: 'Call Support', icon: Phone },
    { title: 'Email Support', icon: Mail }
  ];

  const filteredCategories = helpCategories.filter(category =>
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.articles.some(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else {
      setSelectedCategory(null);
    }
  };

  const renderMainView = () => (
    <div className="container py-4">
      <div className="text-center mb-5">
        <h1 className="display-5 fw-bold">Help Center</h1>
        <p className="text-muted">Find answers to your questions about ONDC dashboard</p>
      </div>

      <div className="input-group mb-4">
        <span className="input-group-text"><Search size={18} /></span>
        <input
          type="text"
          className="form-control"
          placeholder="Search for help articles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="row g-4 mb-5">
        {filteredCategories.map((category) => {
          const IconComponent = category.icon;
          return (
            <div className="col-md-6 col-lg-4" key={category.id}>
              <div className="card h-100 cursor-pointer" onClick={() => handleCategoryClick(category)}>
                <div className="card-body">
                  <div className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-3 p-3 ${category.colorClass}`}>
                    <IconComponent size={24} />
                  </div>
                  <h5 className="card-title">{category.title}</h5>
                  <p className="card-text text-muted">{category.articles.length} articles</p>
                  <a className="text-primary d-flex align-items-center gap-1">
                    View articles <ChevronRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-light p-4 rounded">
        <h4 className="mb-3">Need more help?</h4>
        <div className="row g-3">
          {quickActions.map((action, idx) => {
            const IconComponent = action.icon;
            return (
              <div className="col-md-4" key={idx}>
                <button className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2 py-2">
                  <IconComponent size={18} />
                  {action.title}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderCategoryView = () => (
    <div className="container py-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-link p-0" onClick={handleBack}>
          <ArrowLeft size={20} />
        </button>
        <div className="d-flex align-items-center gap-3">
          <div className={`rounded-circle p-2 ${selectedCategory.colorClass}`}>
            <selectedCategory.icon size={20} />
          </div>
          <div>
            <h2 className="h4 mb-1">{selectedCategory.title}</h2>
            <small className="text-muted">{selectedCategory.articles.length} articles</small>
          </div>
        </div>
      </div>

      <div className="list-group">
        {selectedCategory.articles.map((article) => (
          <div
            key={article.id}
            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            onClick={() => handleArticleClick(article)}
            role="button"
          >
            <div className="d-flex align-items-center gap-2">
              <HelpCircle size={18} className="text-muted" />
              <span>{article.title}</span>
            </div>
            <ChevronRight size={16} className="text-muted" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderArticleView = () => (
    <div className="container py-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-link p-0" onClick={handleBack}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="h4 mb-1">{selectedArticle.title}</h2>
          <small className="text-muted">Last updated: Today</small>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <p className="text-muted">{selectedArticle.content}</p>
          <div className="alert alert-primary mt-3">
            This is a sample article content. In a real implementation, this would contain detailed
            step-by-step instructions, screenshots, and comprehensive information about the topic.
          </div>
        </div>
      </div>

      <div className="bg-light p-4 rounded">
        <h5 className="mb-3">Was this article helpful?</h5>
        <div className="d-flex gap-2">
          <button className="btn btn-success">Yes, it helped</button>
          <button className="btn btn-outline-secondary">No, I need more help</button>
        </div>
      </div>
    </div>
  );

  return selectedArticle
    ? renderArticleView()
    : selectedCategory
    ? renderCategoryView()
    : renderMainView();
};

export default Helpcenter;
